import {useState, type FC} from 'react'
import {type Ruleset, RulesetEnforcement} from '@github-ui/repos-rules-types/rules-types'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {ActionList, ActionMenu, Button, Label} from '@primer/react'
import {clsx} from 'clsx'
import {Link} from '@github-ui/react-core/link'
import {useJsonUpload} from '../hooks/use-json-upload'
import {useRelativeNavigation} from '../hooks/use-relative-navigation'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {TriangleDownIcon} from '@primer/octicons-react'
import type {FlashAlert} from '@github-ui/dismissible-flash'
import {useRuleFeatures} from '../hooks/use-rule-features'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IMPORT_LOCAL_STORAGE_KEY} from '../helpers/constants'

import styles from './NewRulesetButton.module.css'
import {useRuleStrings} from '../hooks/use-rule-strings'

type NewRulesetButtonProps = {
  rulesetsUrl: string
  reloadDocument?: boolean
  defaultEnforcement?: RulesetEnforcement
  className?: string
  setFlashAlert: (flashAlert: FlashAlert) => void
}

export const NewRulesetButton: FC<NewRulesetButtonProps> = ({
  rulesetsUrl,
  reloadDocument,
  className,
  defaultEnforcement = RulesetEnforcement.Disabled,
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  setFlashAlert = () => {},
}) => {
  const [, setImportedRuleset] = useLocalStorage<Ruleset | undefined>(IMPORT_LOCAL_STORAGE_KEY, undefined)
  const {supportedTargets, importExportEnabled, targetDefinitions} = useRuleFeatures()
  const {rulesetOrPolicy} = useRuleStrings()

  const lifecycleLabelNameEnabled = isFeatureEnabled('lifecycle_label_name_updates')
  const {navigate} = useRelativeNavigation()
  const [menuOpen, setMenuOpen] = useState(false)

  const actions = supportedTargets
    .map(target => {
      const targetDefinition = targetDefinitions[target]
      if (!targetDefinition) {
        return null
      }
      return {
        beta: targetDefinition.beta,
        text: targetDefinition.createButtonText,
        href: `${rulesetsUrl}new?target=${target}&enforcement=${defaultEnforcement}`,
      }
    })
    .filter(x => x !== null)

  const {inputRef, isUploading, handleUploadIntent, handleUpload} = useJsonUpload()

  if (actions.length === 0) {
    return (
      <Button variant="primary" disabled>
        {`New ${rulesetOrPolicy}`}
      </Button>
    )
  }

  if (actions.length === 1 && !importExportEnabled) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const {href, text} = actions[0]!
    return (
      <Button variant="primary" type="button" as={Link} to={href} reloadDocument={reloadDocument}>
        {text}
      </Button>
    )
  }

  return (
    <div className={clsx(className, styles.NewRulesetButtonContainer)}>
      <ActionMenu open={menuOpen} onOpenChange={() => setMenuOpen(!menuOpen)}>
        <ActionMenu.Anchor>
          <Button variant="primary" trailingAction={TriangleDownIcon}>
            {`New ${rulesetOrPolicy}`}
          </Button>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay>
          <ActionList>
            {actions.map(action => (
              <ActionList.LinkItem
                as={Link}
                key={action.text}
                to={action.href}
                /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                className="text-decoration-skip"
                reloadDocument={reloadDocument}
              >
                {action.text}
                {action.beta && (
                  <ActionList.TrailingVisual>
                    {lifecycleLabelNameEnabled ? <BetaLabel /> : <Label variant="success">Beta</Label>}
                  </ActionList.TrailingVisual>
                )}
              </ActionList.LinkItem>
            ))}
            {importExportEnabled && (
              <>
                <ActionList.Divider />
                <ActionList.Item onSelect={handleUploadIntent} disabled={isUploading}>
                  <span className="text-normal">Import a ruleset</span>
                  <ActionList.Description variant="block" className="no-wrap">
                    Choose a JSON file to upload
                  </ActionList.Description>
                </ActionList.Item>
                <input
                  hidden
                  ref={inputRef}
                  type="file"
                  accept=".json"
                  onChange={async event => {
                    try {
                      const importedRuleset = await handleUpload(event.target.files)
                      if (!importedRuleset) {
                        throw new Error('Cannot import an empty ruleset')
                      }
                      if (importExportEnabled) {
                        const navigationResult = await verifiedFetchJSON(`${rulesetsUrl}validate_import`, {
                          method: 'POST',
                          body: importedRuleset,
                        })
                        if (navigationResult.status === 414) {
                          throw new Error(`Error importing ruleset: Query string too long`)
                        }
                        const {message, ruleset} = await navigationResult.json()
                        if (message) {
                          throw new Error(`Error importing ruleset: ${message}`)
                        }
                        setImportedRuleset(ruleset)
                        navigate(`${rulesetsUrl}new`, `imported_ruleset=1&target=${encodeURIComponent(ruleset.target)}`)
                      } else {
                        const navigationResult = await verifiedFetchJSON(
                          `${rulesetsUrl}new?imported_ruleset=${encodeURIComponent(JSON.stringify(importedRuleset))}`,
                          {method: 'GET'},
                        )
                        const result = await navigationResult.json()
                        if (result.errors) {
                          throw new Error(`Error importing ruleset: ${result.errors}`)
                        }
                        navigate(
                          `${rulesetsUrl}new`,
                          `imported_ruleset=${encodeURIComponent(JSON.stringify(importedRuleset))}`,
                        )
                      }
                    } catch (error) {
                      setMenuOpen(false)
                      setFlashAlert({message: (error as Error).message || 'Error importing ruleset', variant: 'danger'})
                    }
                  }}
                />
              </>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}
